import { useEffect, useRef, useState, useMemo, memo } from "react";
import { embedSrc } from "../../lib/constants";
import { getTextEffectStyle, strokeLayerStyles } from "../../lib/textEffects";
import { WARPS } from "../../lib/textWarp";
import { getImageFilter } from "../../lib/imageEffects";
import { buildAdjustments } from "../../lib/adjustments";
import { effLetterSpacing, lineHeightPx, alignToCss, kerningCss, smoothingCss, buildTextTransform, fitLineSizes } from "../../lib/typography";

function WarpedText({ el, scale }) {
  const { style: fx } = getTextEffectStyle(el, scale);
  const base = {
    fontFamily: `'${el.fontFamily}', sans-serif`,
    fontSize: el.fontSize * scale,
    color: el.color,
    fontWeight: el.fontWeight,
    fontStyle: el.fontStyle,
    letterSpacing: effLetterSpacing(el, scale),
    ...fx,
  };
  const lines = (el.text || "").split("\n");
  const a = (el.warpBend ?? 50) / 100;
  const fn = WARPS[el.warp] || WARPS.arch;
  const justify = el.textAlign === "left" ? "flex-start" : el.textAlign === "right" ? "flex-end" : "center";
  return (
    <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", overflow: "visible", lineHeight: el.lineHeight }}>
      {lines.map((line, li) => {
        const chars = [...line];
        const n = Math.max(chars.length, 1);
        return (
          <div key={li} style={{ display: "flex", justifyContent: justify, whiteSpace: "pre", lineHeight: el.lineHeight, width: "100%" }}>
            {chars.map((ch, i) => {
              const tx = n === 1 ? 0 : ((i + 0.5) / n) * 2 - 1;
              const t = fn(tx, a);
              return (
                <span key={i} style={{ ...base, display: "inline-block", transform: `translateY(${t.ty}em) scale(${t.sx}, ${t.sy}) rotate(${t.rot}deg)`, transformOrigin: t.o, whiteSpace: "pre" }}>
                  {ch === " " ? "\u00A0" : ch}
                </span>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function CurvedText({ el, scale }) {
  const W = el.width, H = el.height;
  const fs = el.fontSize || 40;
  const amount = (el.curve || 0) / 100;
  const s = Math.abs(amount) * H * 0.5;
  const id = `curve-${el.id}`;
  let d, startY;
  if (s < 1) {
    startY = H / 2;
    d = `M 0 ${startY} L ${W} ${startY}`;
  } else {
    const R = (W * W / 4 + s * s) / (2 * s);
    if (amount > 0) { startY = H / 2 + s / 2; d = `M 0 ${startY} A ${R} ${R} 0 0 0 ${W} ${startY}`; }
    else { startY = H / 2 - s / 2; d = `M 0 ${startY} A ${R} ${R} 0 0 1 ${W} ${startY}`; }
  }
  const { style } = getTextEffectStyle(el, 1);
  return (
    <svg width={W * scale} height={H * scale} viewBox={`0 0 ${W} ${H}`} style={{ overflow: "visible" }}>
      <defs><path id={id} d={d} fill="none" /></defs>
      <text
        fill={style.color || el.color}
        fontFamily={`'${el.fontFamily}', sans-serif`}
        fontSize={fs}
        fontWeight={el.fontWeight}
        fontStyle={el.fontStyle}
        letterSpacing={effLetterSpacing(el, 1)}
        style={{ textShadow: style.textShadow, WebkitTextStroke: style.WebkitTextStroke, paintOrder: style.paintOrder }}
      >
        <textPath href={`#${id}`} startOffset="50%" textAnchor="middle">{el.text}</textPath>
      </text>
    </svg>
  );
}

// ---- Per-word styling (character-range runs) ----
// el.runs = [{ start, end, color?, strokeColor?, strokeWidth? }] over el.text char offsets.
// Properties resolve independently (last matching run wins per property) so color-only and
// stroke-only runs compose.
function resolveRunStyleAt(i, runs) {
  let color, strokeColor, strokeWidth;
  for (const r of runs) {
    if (i >= r.start && i < r.end) {
      if (r.color != null) color = r.color;
      if (r.strokeWidth != null) { strokeWidth = r.strokeWidth; strokeColor = r.strokeColor; }
    }
  }
  return { color, strokeColor, strokeWidth };
}
function buildRunSegments(lineText, lineStart, runs) {
  const segs = [];
  let cur = null;
  for (let k = 0; k < lineText.length; k++) {
    const st = resolveRunStyleAt(lineStart + k, runs);
    const key = `${st.color || ""}|${st.strokeColor || ""}|${st.strokeWidth || ""}`;
    if (cur && cur.key === key) cur.text += lineText[k];
    else { cur = { key, text: lineText[k], st }; segs.push(cur); }
  }
  return segs;
}
function runSpanStyle(st, scale) {
  const s = {};
  if (st.color != null) s.color = st.color;
  if (st.strokeWidth) { s.WebkitTextStroke = `${st.strokeWidth * 2 * scale}px ${st.strokeColor || "#000000"}`; s.paintOrder = "stroke fill"; }
  return s;
}

// Renders a single design element with drag / resize / rotate when interactive.
function CanvasElement({
  el, scale, isSelected, interactive = true, simplify = false, exportPoster, multi = false, hideMedia = false,
  onSelect, onUpdate, onInteractionStart, onCommit, onTextEditingChange, onWordSelect,
  onGroupDragStart, onGroupMove, computeSnap, onGuides,
}) {
  const opRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const textRef = useRef(null);
  const mediaRef = useRef(null);

  // Dynamic text = per-line "fill width": each line scales so its no-wrap width fills the box.
  const fitLines = useMemo(
    () => (el.type === "text" && el.autoFit && !simplify && (!el.warp || el.warp === "none") && !el.curve
      ? fitLineSizes(el) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [el.type, el.autoFit, simplify, el.warp, el.curve, el.text, el.width, el.fontSize, el.fontFamily, el.fontWeight, el.fontStyle, el.tracking]
  );

  useEffect(() => {
    const m = mediaRef.current;
    if (m && m.tagName === "VIDEO") {
      m.muted = el.muted !== false;
      m.volume = el.volume ?? 1;
    }
  }, [el.muted, el.volume, el.src, el.url]);

  useEffect(() => {
    if (!interactive) return;
    const move = (e) => {
      const op = opRef.current;
      if (!op) return;
      const dx = (e.clientX - op.startX) / scale;
      const dy = (e.clientY - op.startY) / scale;
      if (op.type === "groupdrag") { onGroupMove && onGroupMove(dx, dy); return; }
      const o = op.orig;
      if (op.type === "drag") {
        const rawX = o.x + dx, rawY = o.y + dy;
        if (computeSnap) {
          const s = computeSnap(el.id, rawX, rawY, o.width, o.height);
          onUpdate({ x: Math.round(s.x), y: Math.round(s.y) });
          onGuides && onGuides(s.guides);
        } else {
          onUpdate({ x: Math.round(rawX), y: Math.round(rawY) });
        }
      } else if (op.type === "resize") {
        let { x, y, width, height } = o;
        const dir = op.dir;
        if (dir.includes("e")) width = Math.max(10, o.width + dx);
        if (dir.includes("s")) height = Math.max(10, o.height + dy);
        if (dir.includes("w")) { width = Math.max(10, o.width - dx); x = o.x + (o.width - width); }
        if (dir.includes("n")) { height = Math.max(10, o.height - dy); y = o.y + (o.height - height); }
        onUpdate({ x: Math.round(x), y: Math.round(y), width: Math.round(width), height: Math.round(height) });
      } else if (op.type === "rotate") {
        const cx = op.cx, cy = op.cy;
        const ang = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI) + 90;
        onUpdate({ rotation: Math.round(ang) });
      }
    };
    const up = () => {
      if (opRef.current) { opRef.current = null; onCommit && onCommit(); onGuides && onGuides({ v: [], h: [] }); }
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [scale, onUpdate, onCommit, interactive, computeSnap, onGuides, onGroupMove, el.id]);

  const startDrag = (e) => {
    if (!interactive || editing) return;
    e.stopPropagation();
    const additive = e.shiftKey || e.ctrlKey || e.metaKey;
    if (additive) { onSelect && onSelect(true); return; }
    if (multi) {
      onGroupDragStart && onGroupDragStart();
      opRef.current = { type: "groupdrag", startX: e.clientX, startY: e.clientY };
      return;
    }
    onSelect && onSelect(false);
    onInteractionStart && onInteractionStart();
    opRef.current = { type: "drag", startX: e.clientX, startY: e.clientY, orig: { ...el } };
  };

  const startResize = (e, dir) => {
    e.stopPropagation();
    onInteractionStart && onInteractionStart();
    opRef.current = { type: "resize", dir, startX: e.clientX, startY: e.clientY, orig: { ...el } };
  };

  const startRotate = (e) => {
    e.stopPropagation();
    onInteractionStart && onInteractionStart();
    const rect = e.currentTarget.closest("[data-el-root]").getBoundingClientRect();
    opRef.current = { type: "rotate", cx: rect.left + rect.width / 2, cy: rect.top + rect.height / 2, startX: e.clientX, startY: e.clientY, orig: { ...el } };
  };

  const commitText = () => {
    setEditing(false);
    onTextEditingChange && onTextEditingChange(false);
    const txt = textRef.current ? textRef.current.innerText : el.text;
    if (txt !== el.text) { onInteractionStart && onInteractionStart(); onUpdate({ text: txt }); onCommit && onCommit(); }
  };

  // Capture the current text selection as character offsets into el.text (for per-word styling).
  const captureSelection = () => {
    if (!onWordSelect) return;
    const root = textRef.current;
    const sel = typeof window !== "undefined" ? window.getSelection() : null;
    if (!root || !sel || sel.rangeCount === 0) return;
    const r = sel.getRangeAt(0);
    if (!root.contains(r.commonAncestorContainer)) return;
    const pre = r.cloneRange();
    pre.selectNodeContents(root);
    pre.setEnd(r.startContainer, r.startOffset);
    const start = pre.toString().length;
    const end = start + r.toString().length;
    onWordSelect(end > start ? { elId: el.id, start, end } : null);
  };

  const outer = {
    position: "absolute",
    left: el.x * scale,
    top: el.y * scale,
    width: el.width * scale,
    height: el.height * scale,
    transform: `rotate(${el.rotation || 0}deg)`,
    opacity: el.opacity ?? 1,
    zIndex: el.zIndex || 1,
    cursor: interactive ? (editing ? "text" : "move") : "default",
    pointerEvents: interactive ? "auto" : ((el.type === "embed" || el.type === "video") ? "auto" : "none"),
    mixBlendMode: el.blendMode || "normal",
  };

  const renderContent = () => {
    if (el.type === "text") {
      const el2 = el;
      const fsP = el2.fontSize * scale;
      const ls = effLetterSpacing(el2, scale);
      const lh = lineHeightPx(el2, scale);
      const { textAlign, textAlignLast } = alignToCss(el.align || el.textAlign);
      const tform = buildTextTransform(el2, scale);
      const editStyle = {
        width: "100%", height: "100%",
        fontFamily: `'${el.fontFamily}', sans-serif`,
        fontSize: fsP, color: el.color, fontWeight: el.fontWeight, fontStyle: el.fontStyle,
        textAlign, lineHeight: `${lh}px`, letterSpacing: ls,
        whiteSpace: "pre-wrap", wordBreak: "break-word",
        display: "flex", flexDirection: "column", justifyContent: "center",
        outline: "none", overflow: "hidden",
      };
      if (editing) {
        return (
          <div
            ref={textRef}
            contentEditable
            suppressContentEditableWarning
            onBlur={commitText}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); document.execCommand("insertText", false, "\n"); } }}
            onKeyUp={captureSelection}
            onMouseUp={captureSelection}
            style={editStyle}
            data-testid="text-edit-area"
          >
            {el.text}
          </div>
        );
      }
      if (el.warp && el.warp !== "none" && !simplify) {
        return <WarpedText el={el} scale={scale} />;
      }
      if (el.curve && !simplify) {
        return <CurvedText el={el} scale={scale} />;
      }
      const { style: fx, mode, bgColor } = simplify
        ? { style: {}, mode: "plain", bgColor: el.effectColor }
        : getTextEffectStyle(el2, scale);
      const container = {
        width: "100%", height: "100%", display: "flex", flexDirection: "column",
        justifyContent: "center", overflow: "visible",
        fontFamily: `'${el.fontFamily}', sans-serif`, fontSize: fsP, color: el.color,
        fontWeight: el.fontWeight, fontStyle: el.fontStyle, letterSpacing: ls,
        lineHeight: `${lh}px`, fontKerning: kerningCss(el.kerning),
        ...smoothingCss(el.antiAlias),
        transform: tform || undefined, transformOrigin: "center center",
        ...fx,
      };
      if (el.fauxBold && !container.WebkitTextStroke) {
        container.WebkitTextStroke = `${0.022 * fsP}px ${fx.color || el.color}`;
      }
      const paras = (el.text || "").split("\n");
      const runs = Array.isArray(el.runs) && el.runs.length ? el.runs : null;
      let _off = 0;
      const lineStarts = paras.map((p) => { const s = _off; _off += p.length + 1; return s; });
      const renderLineBody = (p, i, extra) => {
        if (extra) return p || "\u200B"; // stroke layer: plain outline, no per-word color
        if (runs) {
          return buildRunSegments(p, lineStarts[i], runs).map((seg, k) => (
            <span key={k} style={runSpanStyle(seg.st, scale)}>{seg.text}</span>
          ));
        }
        return p || "\u200B";
      };
      const paraBase = {
        textAlign, textAlignLast,
        paddingLeft: (el.indentLeft || 0) * scale,
        paddingRight: (el.indentRight || 0) * scale,
        textIndent: (el.indentFirst || 0) * scale,
        whiteSpace: "pre-wrap", wordBreak: "break-word",
      };
      const strokeLayers = simplify ? [] : strokeLayerStyles(el2, scale);
      const perLine = fitLines && !editing ? fitLines : null;
      const lineGap = el.leadingAuto === false ? (el.leading || 0) : 0; // uniform px gap between lines (can be negative)
      const fitScale = el2.fitScale ?? 1;
      const trackPx = ((el2.tracking || 0) / 1000) * 100 * fitScale; // uniform design-px letter-spacing (even across lines)
      const lineStyleFor = (i) => {
        if (!perLine) return null;
        const fz = (perLine[i] ?? el2.fontSize) * fitScale;
        return {
          fontSize: fz * scale,
          lineHeight: `${fz * scale}px`,
          letterSpacing: `${trackPx * scale}px`,
          whiteSpace: "nowrap",
          marginTop: (i === 0 ? 0 : lineGap) * scale,
          marginBottom: 0,
        };
      };
      const renderParas = (extra) =>
        paras.map((p, i) => (
          <div key={i} style={{ ...paraBase, marginTop: i === 0 ? 0 : (el.spaceBefore || 0) * scale, marginBottom: (el.spaceAfter || 0) * scale, ...(lineStyleFor(i) || {}), ...extra }}>
            {mode === "background" && !extra
              ? <span style={{ background: bgColor, padding: `${0.12 * fsP}px ${0.28 * fsP}px`, borderRadius: `${0.1 * fsP}px`, WebkitBoxDecorationBreak: "clone", boxDecorationBreak: "clone" }}>{renderLineBody(p, i, extra)}</span>
              : renderLineBody(p, i, extra)}
          </div>
        ));
      if (strokeLayers.length) {
        return (
          <div style={{ ...container, position: "relative" }}>
            {strokeLayers.map((sl, li) => (
              <div key={`stroke-${li}`} aria-hidden style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "center", zIndex: li }}>
                {renderParas(sl)}
              </div>
            ))}
            <div style={{ position: "relative", zIndex: strokeLayers.length + 1, display: "flex", flexDirection: "column", justifyContent: "center", height: "100%" }}>
              {renderParas()}
            </div>
          </div>
        );
      }
      return (
        <div style={container}>
          {renderParas()}
        </div>
      );
    }
    if (el.type === "rect") {
      return <div style={{ width: "100%", height: "100%", background: el.fill || "transparent", border: el.strokeWidth ? `${el.strokeWidth * scale}px solid ${el.stroke}` : "none", borderRadius: (el.borderRadius || 0) * scale, boxSizing: "border-box" }} />;
    }
    if (el.type === "ellipse") {
      return <div style={{ width: "100%", height: "100%", background: el.fill || "transparent", border: el.strokeWidth ? `${el.strokeWidth * scale}px solid ${el.stroke}` : "none", borderRadius: "50%", boxSizing: "border-box" }} />;
    }
    if (el.type === "line") {
      return <div style={{ width: "100%", height: "100%", background: el.fill || "#000", borderRadius: 999 }} />;
    }
    if (el.type === "image") {
      // Color adjustments are cheap and must always apply (never bypassed by Performance Mode);
      // only the heavy effect filters (shadow/stroke/bevel) are simplified during fast render.
      const { css: adjCss, tableValues } = buildAdjustments(el.adjustments);
      const baseFilter = simplify ? "none" : getImageFilter(el, scale);
      const filterId = `adj-${el.id}`;
      const chain = [
        adjCss,
        tableValues ? `url(#${filterId})` : "",
        baseFilter && baseFilter !== "none" ? baseFilter : "",
      ].filter(Boolean).join(" ") || "none";
      return (
        <>
          {tableValues && (
            <svg width="0" height="0" style={{ position: "absolute", width: 0, height: 0 }} aria-hidden="true">
              <filter id={filterId} colorInterpolationFilters="sRGB">
                <feComponentTransfer>
                  <feFuncR type="table" tableValues={tableValues} />
                  <feFuncG type="table" tableValues={tableValues} />
                  <feFuncB type="table" tableValues={tableValues} />
                </feComponentTransfer>
              </filter>
            </svg>
          )}
          <img src={el.src} crossOrigin="anonymous" alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: (el.borderRadius || 0) * scale, pointerEvents: "none", filter: chain }} />
        </>
      );
    }
    if (el.type === "video") {
      if (hideMedia) return null;
      const vstyle = { width: "100%", height: "100%", objectFit: el.objectFit || "cover", borderRadius: (el.borderRadius || 0) * scale, pointerEvents: "none" };
      if (exportPoster) {
        return <img src={exportPoster} alt="" draggable={false} style={vstyle} />;
      }
      return (
        <video
          ref={mediaRef}
          src={el.src}
          autoPlay={el.autoplay !== false}
          loop={el.playOnce ? false : (el.loop !== false)}
          muted={el.muted !== false}
          playsInline
          crossOrigin="anonymous"
          draggable={false}
          style={vstyle}
        />
      );
    }
    if (el.type === "embed") {
      if (hideMedia) return null;
      const radius = (el.borderRadius || 0) * scale;
      const innerPE = interactive ? "none" : "auto";
      if (el.embedType === "url") {
        return (
          <video
            ref={mediaRef}
            src={el.url}
            autoPlay={el.autoplay !== false}
            loop={el.loop !== false}
            muted={el.muted !== false}
            controls={!interactive}
            playsInline
            draggable={false}
            style={{ width: "100%", height: "100%", objectFit: el.objectFit || "cover", borderRadius: radius, pointerEvents: innerPE }}
          />
        );
      }
      return (
        <div style={{ width: "100%", height: "100%", position: "relative", borderRadius: radius, overflow: "hidden", background: "#000" }}>
          <iframe
            src={embedSrc(el)}
            title="embed"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            style={{ width: "100%", height: "100%", border: "none", pointerEvents: innerPE }}
          />
          {interactive && (
            <span style={{ position: "absolute", top: 6, left: 6, fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: "rgba(0,0,0,0.7)", color: "#fff", textTransform: "capitalize", pointerEvents: "none" }}>
              {el.embedType}
            </span>
          )}
        </div>
      );
    }
    return null;
  };

  const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];
  const handlePos = (h) => {
    const map = {
      nw: { left: -5, top: -5 }, n: { left: "50%", top: -5, ml: -5 },
      ne: { right: -5, top: -5 }, e: { right: -5, top: "50%", mt: -5 },
      se: { right: -5, bottom: -5 }, s: { left: "50%", bottom: -5, ml: -5 },
      sw: { left: -5, bottom: -5 }, w: { left: -5, top: "50%", mt: -5 },
    };
    const p = map[h];
    return {
      position: "absolute", width: 10, height: 10, background: "#fff",
      border: "1.5px solid #3b82f6", borderRadius: 2,
      left: p.left, right: p.right, top: p.top, bottom: p.bottom,
      marginLeft: p.ml, marginTop: p.mt,
      cursor: `${h}-resize`,
    };
  };

  return (
    <div
      data-el-root
      style={outer}
      onMouseDown={startDrag}
      onDoubleClick={(e) => {
        if (el.type === "text" && interactive) {
          e.stopPropagation();
          setEditing(true);
          onTextEditingChange && onTextEditingChange(true);
          setTimeout(() => textRef.current && textRef.current.focus(), 0);
        }
      }}
      data-testid={`canvas-el-${el.id}`}
    >
      {renderContent()}
      {isSelected && !editing && (
        <div style={{ position: "absolute", inset: 0, border: `1.5px solid ${multi ? "#a855f7" : "#3b82f6"}`, pointerEvents: "none", boxSizing: "border-box" }} />
      )}
      {interactive && isSelected && !editing && !multi && (
        <>
          {HANDLES.map((h) => (
            <div key={h} style={handlePos(h)} onMouseDown={(e) => startResize(e, h)} />
          ))}
          {/* rotate handle */}
          <div
            onMouseDown={startRotate}
            style={{ position: "absolute", left: "50%", top: -28, marginLeft: -7, width: 14, height: 14, borderRadius: "50%", background: "#fff", border: "1.5px solid #3b82f6", cursor: "grab" }}
          />
          <div style={{ position: "absolute", left: "50%", top: -18, width: 1.5, height: 18, background: "#3b82f6", marginLeft: -0.75, pointerEvents: "none" }} />
        </>
      )}
    </div>
  );
}

// Skip re-rendering elements that didn't change (big perf win while dragging/typing).
// updateElement only replaces the changed element's object, so unchanged elements keep identity.
export default memo(CanvasElement, (prev, next) =>
  prev.el === next.el &&
  prev.scale === next.scale &&
  prev.isSelected === next.isSelected &&
  prev.interactive === next.interactive &&
  prev.simplify === next.simplify &&
  prev.exportPoster === next.exportPoster &&
  prev.hideMedia === next.hideMedia &&
  prev.multi === next.multi
);
